import type { FlagValue } from '@highlight/analytics'

/**
 * Every part of the sidepanel that has more than one design, and the designs it can take. Each surface is
 * switched in one component, so finishing a migration or scrapping one is deleting a branch there.
 */
export const SIDEPANEL_SURFACES = {
  navigation: {
    label: 'Navigation',
    variants: {
      history: 'Back and forward, with a trail menu',
      home: 'One Home button, no forward history, incognito in the menu',
    },
  },
  upcoming: {
    label: 'Upcoming',
    variants: {
      cards: 'A lead card, then rows with hover buttons',
      rows: 'Flat rows that open a popover',
    },
  },
  home: {
    label: 'Home',
    variants: {
      sections: 'Reorderable sections for Upcoming, For You, Reminders and Recents',
      todos: 'Upcoming Events over one To-dos list',
    },
  },
  meetingView: {
    label: 'Meeting view',
    variants: {
      classic: 'Header, tabs and the action pane',
      'one-column': 'Captured actions over the transcript, no header or tabs',
      tabs: 'Notes and Chat tabs, a live header, one chat thread per meeting',
    },
  },
} as const

export type SidepanelSurface = keyof typeof SIDEPANEL_SURFACES

export type SidepanelSurfaceVariant<S extends SidepanelSurface> = keyof (typeof SIDEPANEL_SURFACES)[S]['variants']

export type SidepanelSurfaces = { [S in SidepanelSurface]: SidepanelSurfaceVariant<S> }

/** The sidepanels people are shown: a design for every surface, released together. */
export const SIDEPANEL_PROFILES = {
  classic: { navigation: 'history', upcoming: 'cards', home: 'sections', meetingView: 'classic' },
  todos: { navigation: 'home', upcoming: 'rows', home: 'todos', meetingView: 'tabs' },
} as const satisfies Record<string, SidepanelSurfaces>

export type SidepanelProfileName = keyof typeof SIDEPANEL_PROFILES

export const DEFAULT_SIDEPANEL_PROFILE: SidepanelProfileName = 'classic'

/** Builds that always get one profile, whatever the flag says. */
const CHANNEL_PROFILES: Partial<Record<string, SidepanelProfileName>> = {
  'adoption-partners': 'todos',
}

/** What decided the profile, in the order they're consulted. */
export type SidepanelProfileSource = 'channel' | 'override' | 'flag' | 'default'

export interface ResolvedSidepanelProfile {
  /** The profile the surfaces start from, before any per-surface override. */
  name: SidepanelProfileName
  source: SidepanelProfileSource
  surfaces: SidepanelSurfaces
}

/** A developer's pick, stored in settings: a whole profile, individual surfaces on top of it, or both. */
export interface SidepanelProfileOverride {
  profile?: SidepanelProfileName
  surfaces?: Partial<SidepanelSurfaces>
}

export interface SidepanelProfileInputs {
  channel: string
  allowsOverride: boolean
  override: SidepanelProfileOverride | null
  /** Called only when the channel and override leave the choice to the flag, so a pinned or overridden build records no exposure. */
  readFlagVariant: () => FlagValue
}

function hasOwnKey(record: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key)
}

export function isSidepanelProfileName(value: unknown): value is SidepanelProfileName {
  return typeof value === 'string' && hasOwnKey(SIDEPANEL_PROFILES, value)
}

function isSidepanelSurface(value: string): value is SidepanelSurface {
  return hasOwnKey(SIDEPANEL_SURFACES, value)
}

/**
 * Picks the sidepanel for this build and person: a channel pin, then a developer override, then the
 * `sidepanel-profile` flag's variant, then the default.
 */
export function resolveSidepanelProfile(inputs: SidepanelProfileInputs): ResolvedSidepanelProfile {
  const pinned = CHANNEL_PROFILES[inputs.channel]
  if (pinned) {
    return describeProfile(pinned, 'channel')
  }

  const override = inputs.allowsOverride ? inputs.override : null
  const base = isSidepanelProfileName(override?.profile)
    ? describeProfile(override.profile, 'override')
    : describeFlagProfile(inputs.readFlagVariant())

  const surfaceOverrides = keepKnownSurfaceVariants(override?.surfaces)
  if (Object.keys(surfaceOverrides).length === 0) {
    return base
  }
  return { ...base, source: 'override', surfaces: { ...base.surfaces, ...surfaceOverrides } }
}

function describeProfile(name: SidepanelProfileName, source: SidepanelProfileSource): ResolvedSidepanelProfile {
  return { name, source, surfaces: SIDEPANEL_PROFILES[name] }
}

function describeFlagProfile(variant: FlagValue): ResolvedSidepanelProfile {
  return isSidepanelProfileName(variant)
    ? describeProfile(variant, 'flag')
    : describeProfile(DEFAULT_SIDEPANEL_PROFILE, 'default')
}

/** Drops stored entries for surfaces and variants that no longer exist. */
function keepKnownSurfaceVariants(surfaces: Partial<SidepanelSurfaces> | undefined): Partial<SidepanelSurfaces> {
  const known: Partial<Record<SidepanelSurface, string>> = {}
  for (const [surface, variant] of Object.entries(surfaces ?? {})) {
    if (
      isSidepanelSurface(surface) &&
      typeof variant === 'string' &&
      hasOwnKey(SIDEPANEL_SURFACES[surface].variants, variant)
    ) {
      known[surface] = variant
    }
  }
  return known as Partial<SidepanelSurfaces>
}
